<script lang="ts" setup>
import { computed, PropType } from "vue";
import { ValidationStateType } from "@/types/Validation";

type Option = Record<string, string | number | null>;

const props = defineProps({
  value: {
    validator: prop =>
      typeof prop === "string" || typeof prop === "number" || prop === null,
    required: true
  },
  formOptions: {
    type: Array as PropType<Option[]>,
    required: true
  },
  textField: {
    type: String,
    default: "text"
  },
  valueField: {
    type: String,
    default: "value"
  },
  disabled: {
    type: Boolean,
    default: false
  },
  firstOption: {
    type: String,
    default: "項目を選択して下さい"
  },
  validationState: {
    type: Object as PropType<ValidationStateType>
  },
  selectedOptions: {
    type: Array as PropType<Option[]>,
    default: () => []
  },
  excludeSelected: {
    type: Boolean,
    default: false
  }
});

const isInvalid = computed(() => {
  return (
    props.validationState?.errors && props.validationState?.errors.length > 0
  );
});

const displayFormOptions = computed(() => {
  if (!props.excludeSelected) {
    return props.formOptions;
  }
  return props.formOptions.filter(option => {
    return !props.selectedOptions
      .filter(item => item[props.valueField] !== props.value)
      .some(item => item[props.valueField] === option[props.valueField]);
  });
});

const selectedOption = computed({
  get() {
    return props.value;
  },
  set(newVal) {
    if (props.value !== newVal) emits("input", newVal);
  }
});

const emits = defineEmits(["input", "change", "mousedown"]);

function onChange(v: string) {
  emits("change", v);
}

function onMouseDown() {
  emits("mousedown");
}
</script>

<template>
  <div
    class="select-wrapper"
    @mousedown="onMouseDown"
    data-testid="select-wrapper"
  >
    <BFormSelect
      class="app-select"
      :class="{
        'text-gray-04': value === null,
        'is-invalid': isInvalid
      }"
      v-model="selectedOption"
      :options="displayFormOptions"
      :text-field="textField"
      :value-field="valueField"
      :disabled="disabled"
      @change="onChange"
    >
      <template v-slot:first>
        <BFormSelectOption :value="null" disabled>
          {{ firstOption }}
        </BFormSelectOption>
      </template>
    </BFormSelect>
  </div>
</template>

<style lang="scss" scoped>
.select-wrapper {
  user-select: none;
}

.app-select {
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: 8px;
  color: $primary-dark;

  &:disabled {
    color: $gray-05;
    background-color: $gray-03;
    opacity: 1;
  }
}

.is-invalid {
  border-color: #ced4da;
}
</style>
